import { CalendarList } from "@/components/calendars/CalendarList.tsx";
import { ConnectCalendarSection } from "@/components/calendars/ConnectCalendarSection.tsx";
import { useCalendarVisibilityToggle } from "@/components/calendars/useCalendarVisibilityToggle.ts";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import { desktopViewsForBreakpoint } from "@/components/canvas/useSharedCalendarState.ts";
import { useMediaQuery } from "@/hooks/useMediaQuery.tsx";
import { cn } from "@/lib/utils.ts";
import { Check, Columns2, Columns3, Grid3x3, Square, X } from "lucide-react";
import type { ComponentType } from "react";
import type { ViewType } from "./types.ts";

type ViewOption = {
  value: ViewType;
  label: string;
  icon: ComponentType<{ className?: string }>;
};

// Mirrors native's CalendarSettingsSheet option list exactly: the two
// iOS-style day layouts plus Month. No Week — native has no week view, and
// the web week grid has no compact-width polish.
const VIEW_OPTIONS: ViewOption[] = [
  { value: "day", label: "1 day", icon: Square },
  { value: "multi-day", label: "2 days", icon: Columns2 },
  { value: "month", label: "Month", icon: Grid3x3 },
];

// How the desktop rows are drawn. Which of them a given width may show is
// `desktopViewsForBreakpoint`'s answer, not this table's — the header's picker
// asks the same question, and the two must not diverge.
const DESKTOP_VIEW_OPTIONS: Partial<Record<ViewType, ViewOption>> = {
  day: { value: "day", label: "Day", icon: Square },
  week: { value: "week", label: "Week", icon: Columns3 },
  month: { value: "month", label: "Month", icon: Grid3x3 },
};

/**
 * The view-switcher rows both options surfaces carry.
 *
 * Choosing one closes the surface: the choice is the whole reason it was
 * opened, and leaving it up would hide the calendar the user just re-laid.
 * Visibility toggles below deliberately do not, so several calendars can be
 * flipped in one visit.
 */
const CalendarViewRows = ({
  options,
  viewType,
  onViewTypeChange,
  onDone,
}: {
  options: ViewOption[];
  viewType: ViewType;
  onViewTypeChange: (v: ViewType) => void;
  onDone: () => void;
}) => (
  <div className="flex flex-col gap-0.5">
    {options.map(({ value, label, icon: Icon }) => {
      const selected = viewType === value;
      return (
        <Button
          key={value}
          variant="ghost"
          className={cn(
            "h-auto w-full justify-start gap-3 rounded-lg px-2 py-2.5",
            // High-contrast selection: a white row over the surface's
            // background, with the standard trailing checkmark.
            selected && "bg-popover",
          )}
          aria-pressed={selected}
          data-statsig-name="calendar-options-view"
          onClick={() => {
            onViewTypeChange(value);
            onDone();
          }}
        >
          <Icon className="size-5" />
          {label}
          {selected && <Check className="ml-auto size-4" />}
        </Button>
      );
    })}
  </div>
);

/**
 * Which calendars are visible, and how to connect another — the half of the
 * calendar-options surface that both breakpoints show.
 *
 * Visibility is self-hosted through the same hook every other calendar surface
 * uses, so each one writes the same server preference rather than keeping its
 * own idea of what is hidden.
 */
const CalendarVisibilityBody = () => {
  const { hiddenCalendarIds, toggleCalendar } = useCalendarVisibilityToggle();

  return (
    <>
      <CalendarList hiddenCalendarIds={hiddenCalendarIds} onToggleCalendar={toggleCalendar} />
      <div className="mt-6">
        <ConnectCalendarSection />
      </div>
    </>
  );
};

/**
 * The desktop calendars surface, reached two ways: the view picker's
 * "Calendars" entry on a wide pane, and the header's sliders icon on a pane too
 * narrow for that picker.
 *
 * It carries the view rows because of the second way in. On a narrow pane this
 * dialog is the only view switcher on screen — the header picker that would
 * otherwise own the job is hidden below `@md`, and without the rows Week and
 * Month had nowhere to be chosen from. On a wide pane they duplicate the picker
 * that opened it, which is the cheaper fault: a second way to switch views
 * costs a glance, no way to switch them costs the view.
 *
 * A dialog rather than the mobile drawer because a bottom sheet claiming 75% of
 * a desktop viewport is a phone idiom out of place, not because the content
 * differs.
 */
export const CalendarsDialog = ({
  open,
  onOpenChange,
  viewType,
  onViewTypeChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewType: ViewType;
  onViewTypeChange: (v: ViewType) => void;
}) => {
  // The same boundary the shared calendar state applies, asked the same way, so
  // the dialog cannot offer a view that state will refuse.
  const options = desktopViewsForBreakpoint(useMediaQuery("lg")).flatMap(
    (view) => DESKTOP_VIEW_OPTIONS[view] ?? [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] w-full max-w-md flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="px-6 pb-2 pt-6">
          <DialogTitle>Calendars</DialogTitle>
          <DialogDescription className="sr-only">
            Choose the calendar view, which calendars are visible, or connect another.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <CalendarViewRows
            options={options}
            viewType={viewType}
            onViewTypeChange={onViewTypeChange}
            onDone={() => onOpenChange(false)}
          />
          <div className="my-4 h-px bg-border" />
          <CalendarVisibilityBody />
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The mobile calendar-options bottom sheet — web's mirror of native's
 * CalendarSettingsSheet, opened from the header's sliders icon. Same content,
 * same order: view-switcher rows (selection shown by row fill; choosing one
 * closes the sheet), a divider, the per-account visibility list, and the
 * connect-calendar affordance. Visibility toggles keep the sheet open so
 * several calendars can be flipped in one visit.
 *
 * This breakpoint has no header view picker — the sliders icon stands in its
 * place — so the rows are the only way to switch views here. They carry no
 * Week: the list mirrors native, which has no week view. The desktop dialog
 * above draws the same rows from its own list, which does.
 *
 * View state is the caller's (the shared canvas nav store, so the choice is
 * durable).
 */
export const CalendarOptionsSheet = ({
  open,
  onOpenChange,
  viewType,
  onViewTypeChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewType: ViewType;
  onViewTypeChange: (v: ViewType) => void;
}) => {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[75dvh]">
        {/* Centered title with a trailing close button; the leading spacer
            keeps the title optically centered, mirroring native's header. */}
        <div className="flex items-center px-4 pb-2 pt-1">
          <div className="size-8 shrink-0" aria-hidden="true" />
          <DrawerTitle className="flex-1 text-center">Calendars</DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 rounded-full bg-secondary"
              aria-label="Close"
              data-statsig-name="calendar-options-close"
            >
              <X className="size-4" />
            </Button>
          </DrawerClose>
        </div>
        <DrawerDescription className="sr-only">
          Choose the calendar view and which calendars are visible.
        </DrawerDescription>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10">
          <CalendarViewRows
            options={VIEW_OPTIONS}
            viewType={viewType}
            onViewTypeChange={onViewTypeChange}
            onDone={() => onOpenChange(false)}
          />
          <div className="my-4 h-px bg-border" />
          <CalendarVisibilityBody />
        </div>
      </DrawerContent>
    </Drawer>
  );
};
